/**
 * Custom BlockNote Blocks for Requirements Editor V2
 *
 * This module exports custom block schemas and components for use with BlockNote:
 * - MarthaQuestionBlock: AI-generated clarifying questions inline in the document
 * - DocumentLinkBlock: Wiki-style [[Document Name]] links
 *
 * Usage:
 * ```typescript
 * import { BlockNoteSchema } from '@blocknote/core';
 * import { customBlockSchema } from './custom-blocks';
 *
 * // Create a schema with custom blocks
 * const schema = BlockNoteSchema.create({
 *   blockSpecs: {
 *     ...defaultBlockSpecs,
 *     ...customBlockSchema,
 *   },
 * });
 *
 * // Use with useCreateBlockNote
 * const editor = useCreateBlockNote({ schema });
 * ```
 */

// Explicit imports needed — `export { X } from './Y'` re-exports do NOT create
// local bindings, so getCustomBlockSchema() would get ReferenceError in production
// bundles (Rollup strips the live binding).
import { marthaQuestionSchema } from './MarthaQuestionBlock';
import { documentLinkSchema } from './DocumentLinkBlock';

// Martha Question Block exports
export {
  MarthaQuestionBlock,
  marthaQuestionSchema,
  insertMarthaQuestion,
  type MarthaQuestionProps,
  type MarthaQuestionStatus,
} from './MarthaQuestionBlock';

// Document Link Block exports
export {
  DocumentLinkBlock,
  documentLinkSchema,
  insertDocumentLink,
  parseDocumentLink,
  documentLinkMark,
  type DocumentLinkProps,
} from './DocumentLinkBlock';

/**
 * Combined custom block schema for BlockNote editor initialization
 *
 * Usage with BlockNoteSchema:
 * ```typescript
 * import { BlockNoteSchema, defaultBlockSpecs } from '@blocknote/core';
 * import { getCustomBlockSchema } from './custom-blocks';
 *
 * const schema = BlockNoteSchema.create({
 *   blockSpecs: {
 *     ...defaultBlockSpecs,
 *     ...getCustomBlockSchema(),
 *   },
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _customBlockSchema: Record<string, any> | null = null;

export function getCustomBlockSchema() {
  if (!_customBlockSchema) {
    // createReactBlockSpec returns a factory — call with () to get the BlockSpec
    _customBlockSchema = {
      marthaQuestion: marthaQuestionSchema(),
      documentLink: documentLinkSchema(),
    };
  }
  return _customBlockSchema;
}

// For backwards compatibility - use getCustomBlockSchema() instead
export const customBlockSchema = {
  get marthaQuestion() { return marthaQuestionSchema(); },
  get documentLink() { return documentLinkSchema(); },
};

/**
 * Type definition for the custom schema
 * Use this when defining your editor's schema type
 */
export type CustomBlockSchema = typeof customBlockSchema;

/**
 * Helper to create a complete schema with custom blocks
 * Merges the default schema with custom blocks
 *
 * @param defaultSchema - The default BlockNote schema
 * @returns Combined schema with custom blocks
 */
export function createCustomSchema<T extends Record<string, unknown>>(
  defaultSchema: T
): T & CustomBlockSchema {
  return {
    ...defaultSchema,
    ...customBlockSchema,
  };
}

/**
 * CSS classes for document link inline styling
 * Add these to your global styles or CSS module
 */
export const documentLinkStyles = `
  .document-link-inline {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #2563eb;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 150ms;
  }

  .document-link-inline:hover {
    background-color: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
  }
`;
